import React, { useState, useEffect, createContext, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import request from '../utils/request';

export interface User {
  id: number;
  username: string | null;
  email: string;
  role: 'admin' | 'staff' | 'customer';
  timezone?: string;
  customer_sub_role?: 'main_account' | 'sub_account' | null;
  customer_code?: string;
  enabled_business_modules?: string[];
}

interface AuthContextType {
  user: User | null;
  displayName: string;
  login: (username: string, password: string) => Promise<User>;
  logout: (redirectTo?: string) => void;
  updateUser: (userData: Partial<User>) => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  const syncUserFromToken = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setUser(null);
        return;
      }
      const response = await request.get<User>('/api/v1/auth/me/');
      setUser(response.data);
    } catch (error) {
      console.error('Auth sync failed:', error);
      localStorage.removeItem('token');
      try {
        localStorage.removeItem('refresh_token');
      } catch {}
      setUser(null);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      await syncUserFromToken();
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== 'token' && event.key !== 'refresh_token') return;
      syncUserFromToken();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const login = async (username: string, password: string): Promise<User> => {
    try {
      const response = await request.post<{ token: string; refresh_token?: string; user: User }>('/api/v1/auth/login/', {
        username,
        password,
      });
      const { token, refresh_token, user } = response.data;
      localStorage.setItem('token', token);
      try {
        if (refresh_token) localStorage.setItem('refresh_token', String(refresh_token));
      } catch {}
      setUser(user);
      return user;
    } catch (error: any) {
      console.error('登录失败:', error.response?.data || error.message);
      throw error;
    }
  };

  const logout = async (redirectTo?: string) => {
    try {
      const rt = localStorage.getItem('refresh_token') || '';
      if (rt) await request.post('/api/v1/auth/logout/', { refresh_token: rt });
      else await request.post('/api/v1/auth/logout/');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      // 清理标签页相关的SessionStorage数据
      try {
        // 清理标签页列表
        sessionStorage.removeItem('dashboard_tabs');
        
        // 清理所有标签页状态
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('tab_state_') || key.startsWith('tab_url_state_')) {
            sessionStorage.removeItem(key);
            console.log('🧹 清理标签页状态:', key);
          }
        });
        
        console.log('✅ 用户登出时SessionStorage清理完成');
      } catch (error) {
        console.error('❌ 清理SessionStorage失败:', error);
      }
      
      localStorage.removeItem('token');
      try { localStorage.removeItem('refresh_token'); } catch {}
      setUser(null);
      navigate(redirectTo || '/login');
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const displayName = (String(user?.username || '').trim() || String(user?.email || '').trim() || '-');

  const value = {
    user,
    displayName,
    login,
    logout,
    updateUser,
    isLoading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 